import type React from "react";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "request-edit:collapsible-sections";

const noop = () => {};

type SectionState = Record<string, boolean>;

function readStoredState(): SectionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) return {};

    const parsed = JSON.parse(raw);

    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function persistState(state: SectionState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

interface CollapsibleSectionsContextValue {
  expandAll: (expanded: boolean) => void;
  isAllExpanded: boolean;
  isSectionExpanded: (sectionId: string) => boolean;
  registerSection: (sectionId: string) => () => void;
  setSectionExpanded: (sectionId: string, expanded: boolean) => void;
}

const CollapsibleSectionsContext =
  createContext<CollapsibleSectionsContextValue>({
    expandAll: noop,
    isAllExpanded: true,
    isSectionExpanded: () => true,
    registerSection: () => noop,
    setSectionExpanded: noop,
  });

export function CollapsibleSectionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [expandedState, setExpandedState] =
    useState<SectionState>(readStoredState);
  const [registeredSections, setRegisteredSections] = useState<string[]>([]);

  useEffect(() => {
    persistState(expandedState);
  }, [expandedState]);

  const registerSection = useCallback((sectionId: string) => {
    setRegisteredSections((prev) =>
      prev.includes(sectionId) ? prev : [...prev, sectionId],
    );

    return () => {
      setRegisteredSections((prev) => prev.filter((id) => id !== sectionId));
    };
  }, []);

  const isSectionExpanded = useCallback(
    (sectionId: string) => expandedState[sectionId] ?? true,
    [expandedState],
  );

  const setSectionExpanded = useCallback(
    (sectionId: string, expanded: boolean) => {
      setExpandedState((prev) => ({ ...prev, [sectionId]: expanded }));
    },
    [],
  );

  const expandAll = useCallback(
    (expanded: boolean) => {
      setExpandedState((prev) => {
        const next = { ...prev };

        for (const sectionId of registeredSections) {
          next[sectionId] = expanded;
        }

        return next;
      });
    },
    [registeredSections],
  );

  const isAllExpanded = useMemo(
    () =>
      registeredSections.every((sectionId) => expandedState[sectionId] ?? true),
    [registeredSections, expandedState],
  );

  const value = useMemo(
    () => ({
      expandAll,
      isAllExpanded,
      isSectionExpanded,
      registerSection,
      setSectionExpanded,
    }),
    [
      expandAll,
      isAllExpanded,
      isSectionExpanded,
      registerSection,
      setSectionExpanded,
    ],
  );

  return (
    <CollapsibleSectionsContext.Provider value={value}>
      {children}
    </CollapsibleSectionsContext.Provider>
  );
}

export function useCollapsibleSections() {
  return useContext(CollapsibleSectionsContext);
}
